import { useEffect, useState } from "react";
import { WorkspaceCardData } from "../components/Interfaces/WorkspaceCard";
import WorkspaceCard from "../components/WorkspaceCard";
import FeaturedWorkspaceCard from "../components/FeaturedWorkspaceCard";
import WorkspaceMap from "../components/GoogleMap";
import { useJsApiLoader } from "@react-google-maps/api";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import GoogleSearchBox from "../components/GoogleSearchBox";
import { AddressComponents } from "../components/AddressComponents";
import { toast } from "react-toastify";

interface FeaturedWorkspacesRequest {
  location?: string;
  latitude?: number;
  longitude?: number;
  radiusKm?: number;
  limit?: number;
}

const libraries: ("places" | "geocoding" | "marker")[] = [
  "places",
  "geocoding",
  "marker",
];

function IndexPage() {
  const navigate = useNavigate();
  const [featuredWorkspaces, setFeaturedWorkspaces] = useState<
    WorkspaceCardData[]
  >([]);
  const [allWorkspaces, setAllWorkspaces] = useState<WorkspaceCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    message: "",
  });
  const [searchLocation, setSearchLocation] = useState("");

  // Load Google Maps API
  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "",
    libraries,
    version: "weekly",
  });

  useEffect(() => {
    fetchFeaturedWorkspaces();
    fetchAllWorkspaces();
  }, []);

  const fetchFeaturedWorkspaces = async () => {
    try {
      setLoading(true);
      const api = axios.create({
        baseURL: "/",
      });

      const payload: FeaturedWorkspacesRequest = {
        radiusKm: 50,
        limit: 12, // Get more workspaces for the layout
      };

      const response = await api.post(
        "/api/Workspace/getFeaturedWorkspaces",
        payload
      );

      const data = response.data;
      const workspacesData = Array.isArray(data)
        ? data
        : data?.workspaces || [];

      setFeaturedWorkspaces(workspacesData as WorkspaceCardData[]);
      setError(null);
    } catch (err) {
      console.error("Error fetching featured workspaces:", err);
      setError("Failed to load featured workspaces");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllWorkspaces = async () => {
    try {
      const api = axios.create({
        baseURL: "/",
      });

      const payload = {
        location: "",
        moveInDate: null,
        capacity: null,
        userId: null,
        page: 1,
        pageSize: 1000, // Fetch a large number to get all workspaces
      };

      const response = await api.post("/api/Workspace/listWorkspaces", payload);

      const data = response.data;
      const workspacesData = Array.isArray(data)
        ? data
        : data?.workspaces || [];

      setAllWorkspaces(workspacesData as WorkspaceCardData[]);
    } catch (err) {
      console.error("Error fetching all workspaces:", err);
    }
  };

  const handleContactFormChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleContactFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const api = axios.create({
        baseURL: "/",
      });

      const response = await api.post("/api/Contact/submit", {
        firstName: contactForm.name.split(" ")[0] || "",
        lastName: contactForm.name.split(" ").slice(1).join(" ") || "",
        email: contactForm.email,
        mobile: contactForm.phone,
        service: contactForm.service,
        message: contactForm.message,
      });

      if (response.data.success) {
        toast.success(response.data.message);
        setContactForm({
          name: "",
          email: "",
          phone: "",
          service: "",
          message: "",
        });
      } else {
        toast.error(
          response.data.errorMessage ||
            "Failed to send your message. Please try again."
        );
      }
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error(
        "An error occurred while sending your message. Please try again later."
      );
    }
  };

  const handleLocationSelect = (address: AddressComponents) => {
    setSearchLocation(address.fullAddress || "");
  };

  const handleSearch = () => {
    // Navigate to workspaces page with search location as state
    navigate("/workspaces", {
      state: {
        searchLocation: searchLocation,
        fromHomePage: true,
      },
    });
  };

  if (loading) {
    return (
      <div
        className="container mx-auto px-4 py-8"
        style={{ backgroundColor: "var(--background)" }}
      >
        <div className="animate-pulse">
          <div className="h-96 bg-gray-300 rounded-lg mb-8"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[...Array(8)].map((_, index) => (
              <div key={index} className="bg-gray-300 rounded-lg h-64"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="container mx-auto px-4 py-8"
      style={{ backgroundColor: "var(--background)" }}
    >
      {/* Hero Search Section */}
      <div className="mb-12 max-w-4xl mx-auto">
        <div className="text-center mb-8">
          <h1 className="font-medium text-2xl md:text-5xl text-gray-900 mb-4">
            Find your perfect workspace
          </h1>
          <p className="text-lg text-gray-600">
            Discover amazing workspaces for your team, anywhere in the world
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-2xl shadow-lg p-2">
          <div className="flex items-center gap-2">
            <div className="flex-1">
              {isGoogleMapsLoaded ? (
                <GoogleSearchBox
                  onPlaceSelected={handleLocationSelect}
                  onInputChange={(value) => setSearchLocation(value)}
                />
              ) : (
                <div className="flex flex-col w-full gap-1 px-4 py-1 rounded-2xl">
                  <div className="text-md font-semibold">Location</div>
                  <input
                    type="text"
                    placeholder="Loading search..."
                    className="w-full focus:outline-none bg-transparent placeholder-gray-500 text-sm"
                    disabled
                  />
                </div>
              )}
            </div>

            {/* Search Button */}
            <button
              className="bg-[var(--primary)] rounded-full p-3 px-6 cursor-pointer hover:shadow-2xl hover:bg-[var(--secondary)] hover:text-white transition-colors whitespace-nowrap font-medium flex-shrink-0"
              onClick={handleSearch}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 640 640"
                strokeWidth={1.5}
                fill="currentColor"
                className="w-8 h-8"
              >
                <path d="M480 272C480 317.9 465.1 360.3 440 394.7L566.6 521.4C579.1 533.9 579.1 554.2 566.6 566.7C554.1 579.2 533.8 579.2 521.3 566.7L394.7 440C360.3 465.1 317.9 480 272 480C157.1 480 64 386.9 64 272C64 157.1 157.1 64 272 64C386.9 64 480 157.1 480 272zM272 416C351.5 416 416 351.5 416 272C416 192.5 351.5 128 272 128C192.5 128 128 192.5 128 272C128 351.5 192.5 416 272 416z" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {!error && featuredWorkspaces.length > 0 && (
        <>
          {/* Featured Workspaces Custom Layout */}
          <div className="mb-12">
            <div className="mb-8 ">
              <h2 className="text-3xl font-bold text-gray-900 mb-3">
                Featured Workspaes
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl ">
                Discover the highest-rated and most recommended workspaces,
                carefully selected for their exceptional quality and customer
                satisfaction.
              </p>
            </div>

            {/* Custom Featured Grid Layout - 5 columns x 7 rows */}
            <div
              className="grid gap-4 mb-8"
              style={{
                gridTemplateColumns: "repeat(5, 1fr)",
                gridTemplateRows: "repeat(7, 120px)",
                minHeight: "840px",
              }}
            >
              {/* Card 1: 3x5 (col-span-3, row-span-5) */}
              {featuredWorkspaces[0] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 3",
                    gridRow: "span 5",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <FeaturedWorkspaceCard {...featuredWorkspaces[0]} />
                  </div>
                </div>
              )}

              {/* Card 2: 1x2.5 (col-span-1, row-span-2.5) - using row-span-3 for simplicity */}
              {featuredWorkspaces[1] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 3",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-gray-400 to-gray-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <FeaturedWorkspaceCard {...featuredWorkspaces[1]} />
                  </div>
                </div>
              )}

              {/* Card 3: 1x2.5 */}
              {featuredWorkspaces[2] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 3",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-orange-600 to-red-600 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <FeaturedWorkspaceCard {...featuredWorkspaces[2]} />
                  </div>
                </div>
              )}

              {/* Card 4: 1x2.5 */}
              {featuredWorkspaces[3] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 2",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <FeaturedWorkspaceCard {...featuredWorkspaces[3]} />
                  </div>
                </div>
              )}

              {/* Card 5: 1x2.5 */}
              {featuredWorkspaces[4] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 2",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <FeaturedWorkspaceCard {...featuredWorkspaces[4]} />
                  </div>
                </div>
              )}

              {/* Cards 6-10: 2x1 (horizontal cards at bottom) */}
              {featuredWorkspaces[5] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 2",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <WorkspaceCard {...featuredWorkspaces[5]} />
                  </div>
                </div>
              )}

              {featuredWorkspaces[6] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 2",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <FeaturedWorkspaceCard {...featuredWorkspaces[6]} />
                  </div>
                </div>
              )}

              {featuredWorkspaces[7] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 2",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <WorkspaceCard {...featuredWorkspaces[7]} />
                  </div>
                </div>
              )}

              {featuredWorkspaces[8] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 2",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <WorkspaceCard {...featuredWorkspaces[8]} />
                  </div>
                </div>
              )}

              {featuredWorkspaces[9] && (
                <div
                  className="relative"
                  style={{
                    gridColumn: "span 1",
                    gridRow: "span 2",
                  }}
                >
                  <div className="absolute top-3 left-3 z-10">
                    <div className="bg-gradient-to-r from-blue-400 to-purple-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center shadow-lg">
                      <svg
                        className="w-3 h-3 mr-1"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                      Featured
                    </div>
                  </div>
                  <div className="h-full">
                    <WorkspaceCard {...featuredWorkspaces[9]} />
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
      <>
        {/* Google Map Section */}
        <div className="mb-12">
          <div className="h-[600px] rounded-lg overflow-hidden shadow-lg">
            {isGoogleMapsLoaded ? (
              <WorkspaceMap
                workspaces={allWorkspaces}
                onWorkspaceSelect={(workspace) => {
                  console.log("Selected workspace:", workspace);
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-200">
                <div className="text-gray-500">Loading map...</div>
              </div>
            )}
          </div>
        </div>

        {/* Workspace Services Section */}
        <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 md:p-12">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Workspace Solutions & Services
              </h2>
              <p className="text-lg text-gray-600">
                Need workspace renovations, installations, or custom solutions?
                We provide comprehensive quotes and professional services to
                transform your space.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-12 items-start">
              {/* Services Info */}
              <div>
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  Our Services
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Office Renovations
                      </h4>
                      <p className="text-gray-600">
                        Complete office makeovers, layout redesign, and
                        modernization projects.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M12.395 2.553a1 1 0 00-1.45-.385c-.345.23-.614.558-.822.88-.214.33-.403.713-.57 1.116-.334.804-.614 1.768-.84 2.734a31.365 31.365 0 00-.613 3.58 2.64 2.64 0 01-.945-1.067c-.328-.68-.398-1.534-.398-2.654A1 1 0 005.05 6.05 6.981 6.981 0 003 11a7 7 0 1011.95-4.95c-.592-.591-.98-.985-1.348-1.467-.363-.476-.724-1.063-1.207-2.03zM12.12 15.12A3 3 0 017 13s.879.5 2.5.5c0-1 .5-4 1.25-4.5.5 1 .786 1.293 1.371 1.879A2.99 2.99 0 0113 13a2.99 2.99 0 01-.879 2.121z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Technology Installations
                      </h4>
                      <p className="text-gray-600">
                        Smart office systems, networking, audio-visual
                        equipment, and automation setup.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm0 4a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Custom Furniture & Fit-outs
                      </h4>
                      <p className="text-gray-600">
                        Bespoke furniture design, ergonomic solutions, and
                        complete workspace fit-outs.
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                      <svg
                        className="w-4 h-4 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900">
                        Maintenance & Support
                      </h4>
                      <p className="text-gray-600">
                        Ongoing maintenance services, emergency repairs, and
                        workspace optimization.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <h3 className="text-2xl font-semibold text-gray-900 mb-6">
                  Get a Quote
                </h3>
                <form onSubmit={handleContactFormSubmit} className="space-y-4">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={contactForm.name}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={contactForm.email}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your email address"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={contactForm.phone}
                      onChange={handleContactFormChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="service"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Service Required *
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={contactForm.service}
                      onChange={handleContactFormChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Select a service</option>
                      <option value="renovation">Office Renovation</option>
                      <option value="technology">
                        Technology Installation
                      </option>
                      <option value="furniture">
                        Custom Furniture & Fit-outs
                      </option>
                      <option value="maintenance">Maintenance & Support</option>
                      <option value="consultation">General Consultation</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Project Details *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={contactForm.message}
                      onChange={handleContactFormChange}
                      required
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Describe your project requirements, timeline, and any specific needs..."
                    />
                  </div>

                  <button type="submit" className="secondary-button w-full">
                    Send Inquiry
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </>
    </div>
  );
}

export default IndexPage;
